import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs, doc, getDoc, updateDoc, limit, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import toast from 'react-hot-toast';
import ConfirmAction from '../../components/common/ConfirmAction';
import { classifyVehicleTier, TIER_INFO } from '../../config/vehicleTiers';
import VEHICLE_MAKES from '../../config/vehicleMakes';

// Small labelled text input used by the driver edit modal.
function EditField({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-[0.85rem] font-semibold mb-1">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || ''}
        className="w-full px-2.5 py-2 rounded-lg border-[1.5px] border-slate-200 text-[0.9rem]" />
    </div>
  );
}

// Status badge styling for driver applications in the review queue.
const APP_BADGE = {
  pending: { bg: '#fef3c7', color: '#92400e', label: 'Pending' },
  documentsSubmitted: { bg: '#dbeafe', color: '#1e40af', label: 'Documents Submitted' },
  underReview: { bg: '#dbeafe', color: '#1e40af', label: 'Under Review' },
  changesRequested: { bg: '#ffedd5', color: '#9a3412', label: 'Changes Requested · awaiting driver' },
};

// ═══════════════ ADMIN OVERVIEW (HOME) ═══════════════
export function AdminOverview() {
  const [stats, setStats] = useState({ activeTrips: 0, todayBookings: 0, todayRevenue: 0, activeDrivers: 0 });
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    // Live count of active trips
    const q = query(collection(db, 'bookings'), where('status', 'in', ['confirmed', 'en_route', 'arrived', 'pin_verified', 'in_progress']));
    const unsub = onSnapshot(q, (snap) => {
      setStats((prev) => ({ ...prev, activeTrips: snap.size }));
    });

    // Today's completed bookings
    const today = new Date(); today.setHours(0, 0, 0, 0);
    getDocs(query(collection(db, 'bookings'), where('status', '==', 'completed'), orderBy('completedAt', 'desc'), limit(100)))
      .then((snap) => {
        const todayTrips = snap.docs.filter((d) => d.data().completedAt?.toDate?.() >= today);
        const revenue = todayTrips.reduce((sum, d) => sum + (d.data().fareBreakdown?.totalFare || 0), 0);
        setStats((prev) => ({ ...prev, todayBookings: todayTrips.length, todayRevenue: revenue }));
      });

    // Active drivers
    getDocs(query(collection(db, 'users'), where('role', '==', 'driver'), where('status', '==', 'active')))
      .then((snap) => setStats((prev) => ({ ...prev, activeDrivers: snap.size })));

    // Recent audit log
    getDocs(query(collection(db, 'auditLog'), orderBy('timestamp', 'desc'), limit(20)))
      .then((snap) => setRecentActivity(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));

    return unsub;
  }, []);

  const cards = [
    { label: 'Active Trips', value: stats.activeTrips, color: '#2dd4a8', icon: '🚗' },
    { label: 'Bookings Today', value: stats.todayBookings, color: '#6366f1', icon: '📋' },
    { label: 'Revenue Today', value: `$${stats.todayRevenue.toFixed(2)}`, color: '#fbbf24', icon: '💰' },
    { label: 'Active Drivers', value: stats.activeDrivers, color: '#8b5cf6', icon: '👤' },
  ];

  return (
    <div>
      <h1 className="font-serif mt-0 mb-6 text-brand-dark">Dashboard</h1>

      <div className="grid gap-4 mb-8" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        {cards.map((card) => (
          <div key={card.label} className="p-5 rounded-2xl bg-white border border-slate-200">
            <div className="flex justify-between items-center mb-2">
              <span className="text-[0.85rem] text-body">{card.label}</span>
              <span className="text-[1.2rem]">{card.icon}</span>
            </div>
            <div className="text-[1.8rem] font-extrabold text-brand-dark">{card.value}</div>
          </div>
        ))}
      </div>

      <h3 className="mt-0 mb-4 text-brand-dark">Recent Activity</h3>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {recentActivity.length === 0 ? (
          <div className="p-8 text-center text-muted">No recent activity</div>
        ) : (
          recentActivity.slice(0, 10).map((entry) => (
            <div key={entry.id} className="px-5 py-3 border-b border-slate-100 flex justify-between items-center">
              <span className="text-[0.85rem] text-brand-dark">{entry.action}</span>
              <span className="text-[0.75rem] text-muted">
                {entry.timestamp?.toDate ? new Date(entry.timestamp.toDate()).toLocaleString() : ''}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ═══════════════ BOOKINGS PANEL ═══════════════
export function BookingsPanel() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    let q = query(collection(db, 'bookings'), orderBy('createdAt', 'desc'), limit(100));
    if (statusFilter !== 'all') {
      q = query(collection(db, 'bookings'), where('status', '==', statusFilter), orderBy('createdAt', 'desc'), limit(100));
    }
    getDocs(q).then((snap) => {
      setBookings(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, [statusFilter]);

  const statuses = ['all', 'pending', 'confirmed', 'en_route', 'arrived', 'in_progress', 'completed', 'cancelled'];

  return (
    <div>
      <h1 className="font-serif mt-0 mb-6">Bookings</h1>
      <div className="flex gap-2 mb-5 flex-wrap">
        {statuses.map((s) => (
          <button key={s} onClick={() => { setStatusFilter(s); setLoading(true); }}
            className={`px-3.5 py-1.5 rounded-full border-[1.5px] cursor-pointer text-[0.8rem] font-semibold ${
              statusFilter === s ? 'border-brand-primary' : 'border-slate-200 bg-white'
            }`}
            style={statusFilter === s ? { background: 'rgba(45,212,168,0.06)' } : undefined}>{s}</button>
        ))}
      </div>

      {loading ? <div className="p-10 text-center text-muted">Loading...</div> : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-auto">
          <table className="w-full border-collapse text-[0.85rem]">
            <thead>
              <tr className="bg-slate-50">
                {['Ref', 'Rider', 'Driver', 'Pickup', 'Time', 'Tier', 'Status', 'Fare'].map((h) => (
                  <th key={h} className="px-3.5 py-3 text-left font-semibold text-body border-b border-slate-200">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} className="border-b border-slate-100 cursor-pointer">
                  <td className="px-3.5 py-2.5 font-semibold">{b.referenceNumber}</td>
                  <td className="px-3.5 py-2.5">{b.riderName || '—'}</td>
                  <td className="px-3.5 py-2.5">{b.driverName || '—'}</td>
                  <td className="px-3.5 py-2.5 max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap">{b.pickupAddress || '—'}</td>
                  <td className="px-3.5 py-2.5">{b.scheduledPickupTime?.toDate ? new Date(b.scheduledPickupTime.toDate()).toLocaleString() : '—'}</td>
                  <td className="px-3.5 py-2.5">{b.tier}</td>
                  <td className="px-3.5 py-2.5"><span className="px-2 py-[3px] rounded-full text-[0.75rem] font-semibold bg-slate-100">{b.status}</span></td>
                  <td className="px-3.5 py-2.5 font-semibold">${b.fareBreakdown?.totalFare?.toFixed(2) || '—'}</td>
                </tr>
              ))}
              {bookings.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-muted">No bookings found</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════ DRIVERS PANEL ═══════════════
export function DriversPanel() {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);

  const [applications, setApplications] = useState([]);
  const [tab, setTab] = useState('active'); // active, applications
  const [loading, setLoading] = useState(true);
  const [overrideTarget, setOverrideTarget] = useState(null);   // driver to override
  const [overrideTier, setOverrideTier] = useState('gold');
  const [overrideExpiry, setOverrideExpiry] = useState('');
  const [statusTarget, setStatusTarget] = useState(null);       // driver to suspend/activate
  const [editTarget, setEditTarget] = useState(null);           // driver being edited (contact + vehicle)
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const reloadDrivers = () => {
    getDocs(query(collection(db, 'users'), where('role', '==', 'driver'), orderBy('createdAt', 'desc')))
      .then((snap) => setDrivers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
  };

  const openEdit = (d) => {
    const v = d.vehicle || {};
    setEditForm({
      firstName: d.firstName || '', lastName: d.lastName || '', phone: d.phone || '',
      make: v.make || '', model: v.model || '', colour: v.colour || '',
      plate: v.plate || '', year: v.year ? String(v.year) : '', seats: v.seats ? String(v.seats) : '',
      tierOverride: v.tierOverride || '',
    });
    setEditTarget(d);
  };

  const handleSaveEdit = async () => {
    if (!editForm.firstName.trim() || !editForm.lastName.trim()) { toast.error('Name is required'); return; }
    setSavingEdit(true);
    try {
      const derivedTier = classifyVehicleTier(editForm.make, editForm.model);
      const effectiveTier = editForm.tierOverride || derivedTier || null;
      const vehicle = {
        ...(editTarget.vehicle || {}),
        make: editForm.make || null,
        model: editForm.model || null,
        colour: editForm.colour.trim() || null,
        plate: editForm.plate.trim() ? editForm.plate.trim().toUpperCase() : null,
        year: editForm.year ? Number(editForm.year) : null,
        seats: editForm.seats ? Number(editForm.seats) : null,
        tier: effectiveTier,
        tierOverride: editForm.tierOverride || null,
      };
      await updateDoc(doc(db, 'users', editTarget.id), {
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim(),
        phone: editForm.phone.trim(),
        vehicle,
        serviceTier: effectiveTier,
        updatedAt: serverTimestamp(),
      });
      toast.success('Driver updated');
      setEditTarget(null); setEditForm(null);
      reloadDrivers();
    } catch (err) {
      toast.error(err.message || 'Update failed');
    }
    setSavingEdit(false);
  };

  const handleSubmitOverride = async (reason) => {
    try {
      const fn = httpsCallable(functions, 'setDriverTierOverride');
      await fn({
        driverId: overrideTarget.id,
        tier: overrideTier,
        expiresAt: overrideExpiry || null,
        reason,
      });
      toast.success(`${overrideTarget.firstName}'s tier set to ${overrideTier}`);
      setOverrideTarget(null);
      setOverrideTier('gold');
      setOverrideExpiry('');
      reloadDrivers();
    } catch (err) {
      toast.error(err.message || 'Override failed');
    }
  };

  const handleSubmitStatus = async (reason) => {
    const newStatus = statusTarget.status === 'active' ? 'suspended' : 'active';
    try {
      const fn = httpsCallable(functions, 'setUserStatus');
      await fn({ userId: statusTarget.id, status: newStatus, reason });
      toast.success(`Driver ${newStatus === 'suspended' ? 'suspended' : 'reactivated'}`);
      setStatusTarget(null);
      reloadDrivers();
    } catch (err) {
      toast.error(err.message || 'Status change failed');
    }
  };

  useEffect(() => {
    Promise.all([
      getDocs(query(collection(db, 'users'), where('role', '==', 'driver'), orderBy('createdAt', 'desc'))),
      getDocs(query(collection(db, 'driverApplications'), where('status', 'in', ['pending', 'documentsSubmitted', 'underReview', 'changesRequested']), orderBy('createdAt', 'desc'))),
    ]).then(async ([dSnap, aSnap]) => {
      setDrivers(dSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      const apps = aSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Join each application with its user doc for name / reference number / email
      const enriched = await Promise.all(apps.map(async (a) => {
        try {
          const uSnap = await getDoc(doc(db, 'users', a.userId || a.id));
          const u = uSnap.exists() ? uSnap.data() : {};
          return {
            ...a,
            _name: `${u.firstName || a.step1?.firstName || ''} ${u.lastName || a.step1?.lastName || ''}`.trim(),
            _email: u.email || a.step1?.email || '',
            _ref: u.referenceNumber || '',
          };
        } catch (e) {
          return { ...a, _name: a.step1 ? `${a.step1.firstName || ''} ${a.step1.lastName || ''}`.trim() : '', _email: a.step1?.email || '', _ref: '' };
        }
      }));
      setApplications(enriched);
      setLoading(false);
    });
  }, []);

  const handleApprove = async (appId, userId) => {
    try {
      await updateDoc(doc(db, 'driverApplications', appId), { status: 'approved', reviewedAt: serverTimestamp() });
      await updateDoc(doc(db, 'users', userId), { status: 'active', updatedAt: serverTimestamp() });
      toast.success('Driver approved');
      setApplications((prev) => prev.filter((a) => a.id !== appId));
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div>
      <h1 className="font-serif mt-0 mb-6">Drivers</h1>
      <div className="flex gap-3 mb-5">
        <button onClick={() => setTab('active')} className={tab === 'active' ? 'btn btn-primary' : 'btn btn-secondary'}>
          Active Drivers ({drivers.filter((d) => d.status === 'active').length})
        </button>
        <button onClick={() => setTab('applications')} className={tab === 'applications' ? 'btn btn-primary' : 'btn btn-secondary'}>
          Applications ({applications.length})
        </button>
      </div>

      {loading ? <div className="p-10 text-center text-muted">Loading...</div> : tab === 'active' ? (
        <div className="grid gap-3">
          {drivers.filter((d) => d.status === 'active' || d.status === 'suspended').map((d) => {
            const tier = d.driverStats?.adminOverrideTier || d.driverStats?.qualityTier || 'gold';
            const tierBadge = { gold: { bg: '#fef3c7', color: '#92400e' }, silver: { bg: '#f1f5f9', color: '#475569' }, bronze: { bg: '#fff1e6', color: '#9a3412' }, probation: { bg: '#fee2e2', color: '#991b1b' } }[tier];
            const isOverride = !!d.driverStats?.adminOverrideTier;
            return (
              <div key={d.id} className="p-4 rounded-2xl bg-white border border-slate-200 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center font-bold">{d.firstName?.[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold">{d.firstName} {d.lastName}</div>
                  <div className="text-[0.85rem] text-body">{d.referenceNumber} · {d.email}</div>
                  <div className="text-[0.78rem] text-muted mt-0.5">
                    Rating: ★ {(d.driverStats?.adjustedRating || d.averageRating || 0).toFixed(1)} ·
                    Trips: {d.completedTrips || 0} ·
                    Pts: {Math.max(0, d.currentFYPoints?.totalPoints || 0)}
                    {d.referralCode && <> · Code: <code className="font-mono text-body">{d.referralCode}</code></>}
                  </div>
                  {(() => {
                    const v = d.vehicle || {};
                    const svcTier = v.tier || classifyVehicleTier(v.make, v.model);
                    return (
                      <div className="text-[0.78rem] mt-0.5">
                        {v.make
                          ? <span className="text-body">{[v.colour, v.make, v.model].filter(Boolean).join(' ')}{v.plate && <> · <code className="font-mono">{v.plate}</code></>}</span>
                          : <span className="italic text-muted">No vehicle on file</span>}
                        {svcTier && <> · <span className="font-semibold" style={{ color: TIER_INFO[svcTier]?.colour || '#5a6578' }}>{TIER_INFO[svcTier]?.name || svcTier}</span></>}
                      </div>
                    );
                  })()}
                </div>

                <div className="flex flex-col items-end gap-1">
                  <span className="px-2.5 py-[3px] rounded-full text-[0.7rem] font-bold" style={{ ...tierBadge }}>
                    {tier.toUpperCase()}{isOverride && ' ★'}
                  </span>
                  <span className="px-2.5 py-[3px] rounded-full text-[0.7rem] font-semibold"
                    style={{
                      color: d.status === 'active' ? '#2dd4a8' : '#dc2626',
                      background: d.status === 'active' ? 'rgba(45,212,168,0.1)' : 'rgba(220,38,38,0.1)',
                    }}>
                    {d.status}
                  </span>
                </div>
                <div className="flex flex-col gap-1.5">
                  <button onClick={() => openEdit(d)}
                    className="px-3 py-1.5 rounded-lg border-[1.5px] border-slate-200 bg-white cursor-pointer text-[0.78rem] font-semibold whitespace-nowrap">Edit</button>
                  <button onClick={() => setOverrideTarget(d)}
                    className="px-3 py-1.5 rounded-lg border-[1.5px] border-slate-200 bg-white cursor-pointer text-[0.78rem] font-semibold whitespace-nowrap">Override Tier</button>
                  <button onClick={() => setStatusTarget(d)}
                    className="px-3 py-1.5 rounded-lg border-[1.5px] bg-white cursor-pointer text-[0.78rem] font-semibold whitespace-nowrap"
                    style={{ borderColor: d.status === 'active' ? '#dc2626' : '#2dd4a8', color: d.status === 'active' ? '#dc2626' : '#2dd4a8' }}>{d.status === 'active' ? 'Suspend' : 'Reactivate'}</button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid gap-3">
          {applications.map((a) => {
            const badge = APP_BADGE[a.status] || APP_BADGE.pending;
            return (
            <div key={a.id} onClick={() => navigate(`/admin/drivers/applications/${a.userId || a.id}`)}
              className="p-4 rounded-2xl bg-white border border-slate-200 cursor-pointer transition-all"
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)'; e.currentTarget.style.borderColor = '#2dd4a8'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#e2e8f0'; }}>
              <div className="flex justify-between items-center gap-3">
                <div className="min-w-0">
                  <div className="font-bold">
                    {a._name || 'Unnamed applicant'}
                    {a._ref && <span className="text-[0.8rem] text-muted font-normal"> · {a._ref}</span>}
                  </div>
                  <div className="text-[0.85rem] text-body">
                    {a._email || '—'} · Step {a.currentStep || '?'}/9
                  </div>
                  <div className="text-[0.78rem] text-muted mt-1">
                    Submitted: {a.submittedAt?.toDate ? new Date(a.submittedAt.toDate()).toLocaleString() : '—'}
                    {a.resubmittedAt?.toDate && <> · Resubmitted: {new Date(a.resubmittedAt.toDate()).toLocaleString()}</>}
                  </div>
                </div>
                <span className="px-2.5 py-[3px] rounded-full text-[0.7rem] font-bold whitespace-nowrap" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
              </div>
            </div>
            );
          })}
          {applications.length === 0 && <div className="p-8 text-center text-muted">No pending applications</div>}
        </div>
      )}

      {/* Edit driver (contact + vehicle) modal */}
      {editTarget && editForm && (
        <div className="fixed inset-0 flex items-center justify-center z-[1000] p-5" style={{ background: 'rgba(10,22,40,0.6)' }} onClick={() => { setEditTarget(null); setEditForm(null); }}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 max-w-[560px] w-full max-h-[90vh] overflow-y-auto">
            <h3 className="font-serif mt-0 mb-1">Edit driver</h3>
            <p className="text-body text-[0.85rem] mb-4">{editTarget.referenceNumber} · {editTarget.email}</p>

            <div className="text-[0.75rem] font-bold text-muted uppercase tracking-wide mb-2">Contact</div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <EditField label="First name" value={editForm.firstName} onChange={(v) => setEditForm((f) => ({ ...f, firstName: v }))} />
              <EditField label="Last name" value={editForm.lastName} onChange={(v) => setEditForm((f) => ({ ...f, lastName: v }))} />
              <div className="col-span-2">
                <EditField label="Phone" value={editForm.phone} onChange={(v) => setEditForm((f) => ({ ...f, phone: v }))} />
              </div>
            </div>

            <div className="text-[0.75rem] font-bold text-muted uppercase tracking-wide mb-2">Vehicle</div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-[0.85rem] font-semibold mb-1">Make</label>
                <select value={editForm.make} onChange={(e) => setEditForm((f) => ({ ...f, make: e.target.value, model: '' }))}
                  className="w-full px-2.5 py-2 rounded-lg border-[1.5px] border-slate-200 text-[0.9rem]">
                  <option value="">Select make…</option>
                  {Object.keys(VEHICLE_MAKES).map((mk) => <option key={mk} value={mk}>{mk}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[0.85rem] font-semibold mb-1">Model</label>
                <select value={editForm.model} onChange={(e) => setEditForm((f) => ({ ...f, model: e.target.value }))} disabled={!editForm.make}
                  className="w-full px-2.5 py-2 rounded-lg border-[1.5px] border-slate-200 text-[0.9rem] disabled:bg-slate-50 disabled:text-muted">
                  <option value="">{editForm.make ? 'Select model…' : 'Pick a make first'}</option>
                  {(VEHICLE_MAKES[editForm.make] || []).map((md) => <option key={md} value={md}>{md}</option>)}
                </select>
              </div>
              <EditField label="Colour" value={editForm.colour} onChange={(v) => setEditForm((f) => ({ ...f, colour: v }))} />
              <EditField label="Plate" value={editForm.plate} onChange={(v) => setEditForm((f) => ({ ...f, plate: v.toUpperCase() }))} />
              <EditField label="Year" value={editForm.year} onChange={(v) => setEditForm((f) => ({ ...f, year: v.replace(/\D/g, '').slice(0, 4) }))} />
              <EditField label="Seats" value={editForm.seats} onChange={(v) => setEditForm((f) => ({ ...f, seats: v.replace(/\D/g, '').slice(0, 2) }))} />
            </div>

            {(() => {
              const derived = classifyVehicleTier(editForm.make, editForm.model);
              const effective = editForm.tierOverride || derived;
              return (
                <div className="p-3 rounded-xl bg-slate-50 mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[0.85rem] text-body">Service tier {editForm.tierOverride ? '(overridden)' : '(auto from vehicle)'}</span>
                    {effective && <span className="px-2.5 py-[3px] rounded-full text-[0.72rem] font-bold text-white" style={{ background: TIER_INFO[effective]?.colour || '#5a6578' }}>{TIER_INFO[effective]?.name || effective}</span>}
                  </div>
                  <label className="block text-[0.78rem] font-semibold mb-1 text-muted">Override service tier (optional)</label>
                  <select value={editForm.tierOverride} onChange={(e) => setEditForm((f) => ({ ...f, tierOverride: e.target.value }))}
                    className="w-full px-2.5 py-2 rounded-lg border-[1.5px] border-slate-200 text-[0.9rem]">
                    <option value="">Auto{derived ? ` (${TIER_INFO[derived]?.name || derived})` : ''}</option>
                    {Object.keys(TIER_INFO).map((t) => <option key={t} value={t}>{TIER_INFO[t].name}</option>)}
                  </select>
                  <div className="text-[0.72rem] text-muted mt-1">Sets which trip types this car can serve — separate from the driver's quality rank (Gold/Silver…).</div>
                </div>
              );
            })()}

            <div className="flex gap-2 justify-end">
              <button onClick={() => { setEditTarget(null); setEditForm(null); }} className="px-4 py-2 rounded-lg border-[1.5px] border-slate-200 bg-white cursor-pointer font-semibold">Cancel</button>
              <button onClick={handleSaveEdit} disabled={savingEdit} className="px-4 py-2 rounded-lg border-0 bg-brand-primary text-white cursor-pointer font-semibold">{savingEdit ? 'Saving…' : 'Save changes'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Tier override modal */}
      {overrideTarget && (
        <div className="fixed inset-0 flex items-center justify-center z-[1000] p-5" style={{ background: 'rgba(10,22,40,0.6)' }} onClick={() => setOverrideTarget(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 max-w-[480px] w-full">
            <h3 className="font-serif mt-0 mb-2">Override Tier</h3>
            <p className="text-body text-[0.9rem] mb-4">
              Manually set quality tier for <strong>{overrideTarget.firstName} {overrideTarget.lastName}</strong>.
              Override takes precedence over computed tier until expiry.
            </p>
            <div className="mb-3">
              <label className="block text-[0.85rem] font-semibold mb-1">New Tier</label>
              <select value={overrideTier} onChange={(e) => setOverrideTier(e.target.value)} className="w-full px-2.5 py-2 rounded-lg border-[1.5px] border-slate-200 text-[0.9rem]">
                <option value="gold">Gold</option>
                <option value="silver">Silver</option>
                <option value="bronze">Bronze</option>
                <option value="probation">Probation</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-[0.85rem] font-semibold mb-1">Expires (optional)</label>
              <input type="date" value={overrideExpiry} onChange={(e) => setOverrideExpiry(e.target.value)} className="w-full px-2.5 py-2 rounded-lg border-[1.5px] border-slate-200 text-[0.9rem]" />
              <div className="text-[0.75rem] text-muted mt-1">If empty, override is permanent until manually cleared.</div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setOverrideTarget(null)} className="px-4 py-2 rounded-lg border-[1.5px] border-slate-200 bg-white cursor-pointer font-semibold">Cancel</button>
              <button onClick={async () => {
                const reason = window.prompt('Reason for tier override (required):');
                if (reason && reason.trim().length >= 3) await handleSubmitOverride(reason.trim());
                else if (reason !== null) toast.error('Reason must be at least 3 characters');
              }} className="px-4 py-2 rounded-lg border-0 bg-brand-primary text-white cursor-pointer font-semibold">Apply Override</button>
            </div>
          </div>
        </div>
      )}

      {/* Suspend/activate modal */}
      {statusTarget && (
        <ConfirmAction
          title={statusTarget.status === 'active' ? `Suspend ${statusTarget.firstName}?` : `Reactivate ${statusTarget.firstName}?`}
          description={statusTarget.status === 'active'
            ? 'This driver will not receive new bookings and their active sessions will end.'
            : 'This driver will be able to receive new bookings again.'
          }
          confirmLabel={statusTarget.status === 'active' ? 'Suspend' : 'Reactivate'}
          typedConfirmation={statusTarget.status === 'active' ? 'SUSPEND' : null}
          reasonRequired={true}
          destructive={statusTarget.status === 'active'}
          onConfirm={handleSubmitStatus}
          onCancel={() => setStatusTarget(null)}
        />
      )}
    </div>
  );
}


// ═══════════════ RIDERS PANEL ═══════════════
export function RidersPanel() {
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusTarget, setStatusTarget] = useState(null);

  const reloadRiders = () => {
    getDocs(query(collection(db, 'users'), where('role', '==', 'rider'), orderBy('createdAt', 'desc'), limit(200)))
      .then((snap) => { setRiders(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setLoading(false); });
  };

  useEffect(() => { reloadRiders(); }, []);

  const handleSubmitStatus = async (reason) => {
    const newStatus = statusTarget.status === 'active' ? 'suspended' : 'active';
    try {
      const fn = httpsCallable(functions, 'setUserStatus');
      await fn({ userId: statusTarget.id, status: newStatus, reason });
      toast.success(`Rider ${newStatus === 'suspended' ? 'suspended' : 'reactivated'}`);
      setStatusTarget(null);
      reloadRiders();
    } catch (err) {
      toast.error(err.message || 'Status change failed');
    }
  };

  const filtered = search
    ? riders.filter((r) => `${r.firstName} ${r.lastName} ${r.email} ${r.referenceNumber}`.toLowerCase().includes(search.toLowerCase()))
    : riders;

  return (
    <div>
      <h1 className="font-serif mt-0 mb-6">Riders</h1>
      <input className="form-input mb-5 max-w-[400px]" placeholder="Search by name, email, or reference..." value={search}
        onChange={(e) => setSearch(e.target.value)} />

      {loading ? <div className="p-10 text-center text-muted">Loading...</div> : (
        <div className="grid gap-2">
          {filtered.map((r) => (
            <div key={r.id} className="p-3.5 rounded-xl bg-white border border-slate-200 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center font-semibold text-[0.85rem]">{r.firstName?.[0]}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold">{r.firstName} {r.lastName}</div>
                <div className="text-[0.8rem] text-body">{r.referenceNumber} · {r.email}</div>
              </div>
              <span className="px-2.5 py-[3px] rounded-full text-[0.7rem] font-semibold"
                style={{
                  color: r.status === 'active' ? '#2dd4a8' : '#dc2626',
                  background: r.status === 'active' ? 'rgba(45,212,168,0.1)' : 'rgba(220,38,38,0.1)',
                }}>
                {r.status}
              </span>
              <button onClick={() => setStatusTarget(r)}
                className="px-3 py-1.5 rounded-lg border-[1.5px] bg-white cursor-pointer text-[0.78rem] font-semibold"
                style={{ borderColor: r.status === 'active' ? '#dc2626' : '#2dd4a8', color: r.status === 'active' ? '#dc2626' : '#2dd4a8' }}>{r.status === 'active' ? 'Suspend' : 'Reactivate'}</button>
            </div>
          ))}
          {filtered.length === 0 && <div className="p-8 text-center text-muted">No riders found</div>}
        </div>
      )}

      {statusTarget && (
        <ConfirmAction
          title={statusTarget.status === 'active' ? `Suspend ${statusTarget.firstName}?` : `Reactivate ${statusTarget.firstName}?`}
          description={statusTarget.status === 'active'
            ? 'This rider will not be able to create new bookings.'
            : 'This rider will be able to create new bookings again.'
          }
          confirmLabel={statusTarget.status === 'active' ? 'Suspend' : 'Reactivate'}
          typedConfirmation={statusTarget.status === 'active' ? 'SUSPEND' : null}
          reasonRequired={true}
          destructive={statusTarget.status === 'active'}
          onConfirm={handleSubmitStatus}
          onCancel={() => setStatusTarget(null)}
        />
      )}
    </div>
  );
}

// ═══════════════ VEHICLES PANEL ═══════════════
export function VehiclesPanel() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDocs(query(collection(db, 'vehicles'), orderBy('createdAt', 'desc'), limit(200)))
      .then((snap) => { setVehicles(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setLoading(false); });
  }, []);

  const tierColors = { go: '#2dd4a8', comfy: '#6366f1', plus: '#fbbf24', vip: '#8b5cf6' };

  return (
    <div>
      <h1 className="font-serif mt-0 mb-6">Vehicles</h1>
      {loading ? <div className="p-10 text-center text-muted">Loading...</div> : (
        <div className="grid gap-3">
          {vehicles.map((v) => (
            <div key={v.id} className="p-4 rounded-2xl bg-white border border-slate-200 flex justify-between items-center">
              <div>
                <div className="font-bold">{v.make} {v.model} ({v.year})</div>
                <div className="text-[0.85rem] text-body">Plate: {v.plate} · {v.colour}</div>
              </div>
              <span className="px-3 py-1 rounded-full text-[0.75rem] font-bold"
                style={{ color: tierColors[v.tier] || '#5a6578', background: `${tierColors[v.tier] || '#94a3b8'}15` }}>
                {v.tier?.toUpperCase()}
              </span>
            </div>
          ))}
          {vehicles.length === 0 && <div className="p-8 text-center text-muted">No vehicles registered</div>}
        </div>
      )}
    </div>
  );
}